import React, { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Search, Loader2, Eye, Upload, Download, Check, X, FileText } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import StudentProfileView from "./StudentProfileView";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ENTITIES } from "./masterConfig";

export default function MasterPage({ entity }) {
  const cfg = ENTITIES[entity];
  const { user } = useAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [refs, setRefs] = useState({});
  const [dial, setDial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewSid, setViewSid] = useState(null);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  const parseCsvLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQ = false;
        else cur += ch;
      } else if (ch === '"') inQ = true;
      else if (ch === ",") { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };

  const downloadTemplate = () => {
    const bi = cfg.bulkImport;
    const blob = new Blob([bi.headers.join(",") + "\n" + (bi.sample || "")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${entity}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const lines = String(reader.result).replace(/^\uFEFF/, "").trim().split(/\r?\n/).filter(Boolean);
        if (lines.length < 2) { toast({ title: "CSV is empty", variant: "destructive" }); return; }
        const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
        const items = lines.slice(1).map((l) => {
          const v = parseCsvLine(l); const o = {};
          headers.forEach((h, i) => { o[h] = v[i] || ""; });
          return o;
        }).filter((o) => o.certificate_no && o.student_name && o.programme);
        if (!items.length) { toast({ title: "No valid rows found", description: "Need certificate_no, student_name and programme", variant: "destructive" }); return; }
        const { data } = await api.post(cfg.bulkImport.endpoint, { items });
        toast({ title: `Imported ${data.created} certificate(s)`, description: data.skipped ? `${data.skipped} duplicate(s) skipped` : "All rows added." });
        load();
      } catch (err) {
        toast({ title: "Import failed", description: err?.response?.data?.detail || "Check your CSV format", variant: "destructive" });
      } finally { setImporting(false); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(cfg.endpoint);
      setRows(data);
    } catch (e) {
      toast({ title: "Failed to load", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // load reference collections
    const needed = new Set();
    cfg.fields.forEach((f) => { if (f.ref) needed.add(f.ref); });
    needed.forEach(async (refName) => {
      const ep = refName === "countries" ? "/masters/countries" : refName === "universities" ? "/universities" : `/masters/${refName}`;
      try { const { data } = await api.get(ep); setRefs((p) => ({ ...p, [refName]: data })); } catch {}
    });
    if (cfg.fields.some((f) => f.type === "phone")) {
      api.get("/auth/dial-codes").then((r) => setDial(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [entity]);

  const openCreate = () => {
    const init = {};
    cfg.fields.forEach((f) => { init[f.key] = f.default !== undefined ? f.default : (f.type === "switch" ? true : (f.type === "number" ? 0 : (f.type === "docs" ? [] : ""))); });
    setForm(init); setEditing(null); setOpen(true);
  };
  const openEdit = (row) => {
    const init = {};
    cfg.fields.forEach((f) => { init[f.key] = row[f.key] ?? (f.default !== undefined ? f.default : (f.type === "switch" ? true : (f.type === "docs" ? [] : ""))); });
    if (cfg.fields.some((f) => f.key === "password")) init.password = "";
    setForm(init); setEditing(row); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const q = cfg.approval ? `?origin=${encodeURIComponent(window.location.origin)}` : "";
      if (editing) await api.put(`${cfg.endpoint}/${editing.id}${q}`, form);
      else await api.post(`${cfg.endpoint}${q}`, form);
      toast({ title: `${cfg.singular} ${editing ? "updated" : "created"}` });
      setOpen(false); load();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const onDocs = (key, e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.size > 6 * 1024 * 1024) { toast({ title: `"${file.name}" exceeds 6MB`, variant: "destructive" }); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const kind = file.type.startsWith("image") ? "image" : "file";
        setForm((p) => ({ ...p, [key]: [...(p[key] || []), { id: Math.random().toString(36).slice(2), name: file.name, data: reader.result, kind }] }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeDoc = (key, id) => setForm((p) => ({ ...p, [key]: (p[key] || []).filter((d) => d.id !== id) }));

  const resendVerify = async () => {
    try {
      await api.post(`${cfg.endpoint}/${editing.id}/resend-verification?origin=${encodeURIComponent(window.location.origin)}`);
      toast({ title: "Verification email sent", description: "The agent must click the link to confirm their email before approval." });
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete ${cfg.singular} "${row[cfg.fields[0].key]}"?`)) return;
    await api.delete(`${cfg.endpoint}/${row.id}`);
    toast({ title: `${cfg.singular} deleted` });
    load();
  };

  const approve = async (row) => {
    try {
      await api.post(`${cfg.endpoint}/${row.id}/approve?origin=${encodeURIComponent(window.location.origin)}`);
      toast({ title: "Agent approved", description: "Welcome pack (code + referral link) emailed." });
      load();
    } catch (e) { toast({ title: "Approve failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };
  const reject = async (row) => {
    if (!window.confirm(`Reject "${row.name}"? They'll be notified by email.`)) return;
    try { await api.post(`${cfg.endpoint}/${row.id}/reject`); toast({ title: "Agent rejected" }); load(); }
    catch (e) { toast({ title: "Reject failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };

  const refLabel = (refName, id) => {
    const item = (refs[refName] || []).find((x) => x.id === id);
    return item ? item.name : "—";
  };

  const filtered = rows.filter((r) =>
    JSON.stringify(Object.values(r)).toLowerCase().includes(q.toLowerCase())
  );

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">{cfg.title}</h1>
          <p className="text-slate-500 mt-1 text-sm">{cfg.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {cfg.bulkImport && (
            <>
              <input ref={importRef} type="file" accept=".csv" hidden onChange={onImport} data-testid="cert-import-input" />
              <Button variant="outline" onClick={downloadTemplate} className="rounded-full" data-testid="cert-template-btn"><Download size={16} className="mr-1" /> Template</Button>
              <Button variant="outline" onClick={() => importRef.current?.click()} disabled={importing} className="rounded-full" data-testid="cert-import-btn">
                {importing ? <Loader2 className="animate-spin mr-1" size={16} /> : <Upload size={16} className="mr-1" />} Import CSV
              </Button>
            </>
          )}
          <Button onClick={openCreate} className={`bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5 ${cfg.readOnly ? "hidden" : ""}`}>
            <Plus size={18} className="mr-1" /> Add {cfg.singular}
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm mt-5">
        <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cfg.title.toLowerCase()}...`} className="pl-10 h-11 rounded-full" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 mt-5 overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-slate-400"><Loader2 className="animate-spin inline" /> Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f7faf8] text-left text-slate-500">
                {cfg.columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>)}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-[#f7faf8]">
                  {cfg.columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {c.type === "bool" ? (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r[c.key] ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{r[c.key] ? "Active" : "Inactive"}</span>
                      ) : c.ref ? refLabel(c.ref, r[c.key]) : (c.render ? c.render(r) : (r[c.key] || "—"))}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {cfg.approval && r.status === "pending" && (
                      <>
                        <button onClick={() => approve(r)} className="text-green-600 hover:text-green-700 p-1.5" title="Approve" data-testid={`approve-agent-${r.id}`}><Check size={16} /></button>
                        <button onClick={() => reject(r)} className="text-red-500 hover:text-red-600 p-1.5" title="Reject" data-testid={`reject-agent-${r.id}`}><X size={16} /></button>
                      </>
                    )}
                    {cfg.pdfField && r[cfg.pdfField] && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/certificate-pdf?q=${encodeURIComponent(r[cfg.pdfField])}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-[#004030] p-1.5 inline-block" title="Download PDF" data-testid={`cert-pdf-${r.id}`}><Download size={16} /></a>
                    )}
                    {cfg.viewProfile && <button onClick={() => setViewSid(r.id)} className="text-slate-500 hover:text-[#004030] p-1.5" data-testid={`view-student-${r.id}`}><Eye size={16} /></button>}
                    {!cfg.readOnly && <button onClick={() => openEdit(r)} className="text-slate-500 hover:text-[#004030] p-1.5"><Pencil size={16} /></button>}
                    <button onClick={() => del(r)} className="text-slate-500 hover:text-red-600 p-1.5"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={cfg.columns.length + 1} className="px-4 py-10 text-center text-slate-400">No {cfg.title.toLowerCase()} found.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} {cfg.singular}</DialogTitle></DialogHeader>
          {cfg.approval && editing && (
            <div className={`rounded-xl border p-3 flex items-center justify-between gap-3 ${editing.email_verified ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`} data-testid="agent-verify-block">
              <div>
                <p className="text-sm font-semibold text-[#012a1e]">Email verification</p>
                <p className="text-xs text-slate-500">{editing.email_verified ? "Confirmed by the agent \u2713 — ready to approve." : "Pending — the agent must confirm their email before you can approve & issue a code."}</p>
              </div>
              {!editing.email_verified && <Button size="sm" variant="outline" onClick={resendVerify} className="rounded-full whitespace-nowrap" data-testid="resend-verify-btn">Resend email</Button>}
            </div>
          )}
          <div className="space-y-4 py-2">
            {cfg.fields.map((f) => (
              <div key={f.key} className={f.type === "switch" ? "flex items-center justify-between" : "space-y-2"}>
                <Label>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} className="min-h-20" />
                ) : f.type === "switch" ? (
                  <Switch checked={!!form[f.key]} onCheckedChange={(v) => setF(f.key, v)} />
                ) : f.type === "number" ? (
                  <Input type="number" value={form[f.key] ?? 0} onChange={(e) => setF(f.key, parseFloat(e.target.value) || 0)} className="h-11" />
                ) : f.type === "password" ? (
                  <Input type="password" value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} placeholder={editing ? "Leave blank to keep" : "Default: Welcome@123"} className="h-11" />
                ) : f.type === "phone" ? (
                  <div className="flex gap-2">
                    <Select value={form[f.dialKey || "dial_code"] || "+60"} onValueChange={(v) => setF(f.dialKey || "dial_code", v)}>
                      <SelectTrigger className="h-11 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>{dial.map((d) => <SelectItem key={d.code} value={d.dial}>{d.flag} {d.dial}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} className="h-11 flex-1" placeholder="Phone number" />
                  </div>
                ) : f.type === "email" ? (
                  <Input type="email" value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} className={`h-11 ${form[f.key] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form[f.key]) ? "border-red-400" : ""}`} placeholder="name@example.com" />
                ) : f.type === "docs" ? (
                  <div className="space-y-2">
                    <label className="flex items-center justify-center gap-2 h-11 rounded-xl border border-dashed border-[#004030]/30 text-[#004030] text-sm font-semibold cursor-pointer hover:bg-[#f0f6f3]" data-testid={`docs-upload-${f.key}`}>
                      <Upload size={15} /> Take photo / upload
                      <input type="file" accept="image/*,application/pdf,.doc,.docx" capture="environment" multiple hidden onChange={(e) => onDocs(f.key, e)} />
                    </label>
                    {(form[f.key] || []).length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {(form[f.key] || []).map((d) => (
                          <div key={d.id} className="relative border border-slate-200 rounded-lg p-1.5 text-center" data-testid={`doc-item-${d.id}`}>
                            {d.kind === "image" ? <img src={d.data} alt={d.name} className="h-14 w-full object-cover rounded" /> : <FileText size={24} className="mx-auto text-[#004030] my-1.5" />}
                            <p className="text-[9px] truncate mt-0.5">{d.name}</p>
                            <div className="flex items-center justify-center gap-2 mt-1">
                              <a href={d.data} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-[#004030]" title="View"><Eye size={13} /></a>
                              <a href={d.data} download={d.name} className="text-slate-500 hover:text-[#004030]" title="Download"><Download size={13} /></a>
                              <button type="button" onClick={() => removeDoc(f.key, d.id)} className="text-slate-500 hover:text-red-600" title="Delete"><Trash2 size={13} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : f.type === "options" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setF(f.key, v)}>
                    <SelectTrigger className="h-11"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                    <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.ref ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setF(f.key, v)}>
                    <SelectTrigger className="h-11"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                    <SelectContent>{(refs[f.ref] || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "multiselect" ? (
                  <div className="flex flex-wrap gap-2">
                    {f.options.map((o) => {
                      const arr = form[f.key] || [];
                      const on = arr.includes(o);
                      return (
                        <button type="button" key={o} onClick={() => setF(f.key, on ? arr.filter((x) => x !== o) : [...arr, o])}
                          className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-[#004030] text-white border-[#004030]" : "border-slate-300 text-slate-600"}`}>{o}</button>
                      );
                    })}
                  </div>
                ) : (
                  <Input value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} className="h-11" />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#004030] hover:bg-[#013d2b] text-white">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {cfg.viewProfile && <StudentProfileView open={!!viewSid} onClose={() => setViewSid(null)} studentId={viewSid} />}
    </div>
  );
}
